import { Alert, Group, List, Modal, Stack, Text } from '@mantine/core';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type { RemovalTarget } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/RemoveSigner/RemoveSignerModal.types';

interface Props {
	target: RemovalTarget | null;
	isPending: boolean;
	onConfirm: (signerId: string) => void;
	onClose: () => void;
}

export const RemoveSignerModal = ({ target, isPending, onConfirm, onClose }: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<Modal
			opened={!!target}
			onClose={onClose}
			closeOnClickOutside={false}
			size="lg"
			padding="md"
			radius="md"
			withCloseButton
			centered
			title={
				<Text size="18px" fw={600}>
					{tMipManage('documents_page.remove_signer.modal.title')}
				</Text>
			}
			styles={{
				body: { backgroundColor: '#fff' },
				header: { backgroundColor: '#fff', marginBottom: 0 },
				content: { backgroundColor: '#fff' },
			}}
		>
			<Stack gap="sm">
				<Text size="14px">{tMipManage('documents_page.remove_signer.modal.body')}</Text>

				<List size="sm" withPadding>
					{/* Labels repeat: a PIV shares its investor's email, as do two unnamed witnesses. */}
					{target?.removedNames.map((name, index) => (
						<List.Item key={`${index}-${name}`}>{name}</List.Item>
					))}
				</List>

				<Alert
					color="blue"
					variant="light"
					radius="sm"
					icon={<Icon variant="information" size="lg" />}
				>
					<Text size="13px" c="gray.8">
						{tMipManage('documents_page.remove_signer.modal.warning')}
					</Text>
				</Alert>

				<Group justify="right" gap="xs">
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="tertiary"
						onClick={onClose}
						disabled={isPending}
						data-testid={TEST_ELEMENT_IDS.REMOVE_SIGNER_CANCEL_BUTTON}
					>
						{tMipManage('documents_page.remove_signer.modal.cancel')}
					</ButtonV2>
					<ButtonV2
						size="s"
						buttonTheme="light"
						variant="primary"
						disabled={isPending || !target}
						onClick={() => {
							if (target) {
								onConfirm(target.signerId);
							}
						}}
						data-testid={TEST_ELEMENT_IDS.REMOVE_SIGNER_CONFIRM_BUTTON}
					>
						{tMipManage('documents_page.remove_signer.modal.confirm')}
					</ButtonV2>
				</Group>
			</Stack>
		</Modal>
	);
};
